import { projects } from "../data/projects";
import ProjectCard from "../components/ProjectCard";
import { motion } from "framer-motion";

export default function Projects() {

  // ✅ Safe grouping
  const groupedProjects = projects.reduce((acc, project) => {
    const group = project.group || "Other Projects";

    if (!acc[group]) {
      acc[group] = [];
    }

    acc[group].push(project);
    return acc;
  }, {});

  // ✅ Optional ordering
  const groupOrder = ["Core Systems", "AI Systems", "Client Projects", "Other Projects"];

  const sortedGroups = Object.entries(groupedProjects).sort(
    ([a], [b]) => {
      const indexA = groupOrder.indexOf(a);
      const indexB = groupOrder.indexOf(b);

      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    }
  );

  return (
    <section className="relative py-32 bg-slate-50 overflow-hidden">

      {/* Background Atmosphere */}
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-blue-100/40 blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">

        {/* Header */}
        <div className="text-center mb-20">
          <p className="text-sm uppercase tracking-widest text-blue-600 font-semibold mb-4">
            Portfolio
          </p>

          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900">
            All Projects
          </h1>

          <p className="text-slate-600 mt-6 max-w-2xl mx-auto leading-relaxed">
            A collection of real-world systems, engineering challenges,
            and scalable solutions.
          </p>
        </div>

        {/* Projects */}
        {projects.length > 0 ? (
          sortedGroups.map(([groupName, groupItems]) => {
            const isSystem = groupItems.length > 1;

            return (
              <motion.div
                key={groupName}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-24"
              >

                {/* Group Header */}
                <div className="mb-10">
                  <h2 className="text-2xl font-semibold text-slate-900">
                    {groupName}
                  </h2>

                  <p className="text-slate-500 text-sm">
                    {isSystem
                      ? "Connected system components"
                      : "Standalone project"}
                  </p>
                </div>

                {/* SYSTEM VIEW */}

{isSystem ? (
  <div className="relative group">

    {/* Cards */}
    <div className="grid md:grid-cols-2 gap-10 items-center">
      {groupItems.map((project, index) => (
        <motion.div
          key={project.id}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.6,
            delay: index * 0.1,
          }}
          whileHover={{ scale: 1.05 }}
        >
          <ProjectCard project={project} />
        </motion.div>
      ))}
    </div>

    {/* Connector (desktop only) */}
    <div className="hidden md:flex absolute inset-0 items-center justify-center pointer-events-none">

      <div className="relative flex items-center">

        {/* Base Line */}
        <div className="w-40 h-[2px] bg-slate-300 relative overflow-hidden">

          {/* Animated Flow */}
          <motion.div
            className="absolute top-0 left-0 h-full w-16 bg-blue-500/60"
            animate={{ x: ["-100%", "200%"] }}
            transition={{
              repeat: Infinity,
              duration: 2,
              ease: "linear",
            }}
            
          />

        </div>

        {/* Arrow */}
        <div className="ml-2 text-slate-400 text-xl">→</div>

      </div>

    </div>

    {/* Flow Label */}
    <motion.div
      initial={{ opacity: 0, y: -5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-10 text-xs text-slate-400"
    >
      API → UI Flow
    </motion.div>

  </div>
) : (

                  /* NORMAL GRID */
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
                    {groupItems.map((project, index) => (
                      <motion.div
                        key={project.id}
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.6,
                          delay: index * 0.08,
                        }}
                      >
                        <ProjectCard project={project} />
                      </motion.div>
                    ))}
                  </div>
                )}

              </motion.div>
            );
          })
        ) : (
          <div className="text-center text-slate-500 mt-20">
            No projects available yet.
          </div>
        )}

      </div>
    </section>
  );
}